import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { type IJointBillListPeriodOption } from "@features/JointBillListFilters/JointBillListFilters";
import { Button } from "@components/Button";
import { Dropdown, type IDropdownProps } from "@components/Dropdown/Dropdown";
import { HouseIcon } from "@components/icons";
import {
  MonthPicker,
  type IMonthPickerAvailableDate,
} from "@components/MonthPicker/MonthPicker";

interface IJointBillFiltersProps {
  jointBillServiceOptions: IDropdownProps<string, number>["options"];
  jointBillServiceValue: number;
  periodOptions?: IJointBillListPeriodOption[];
  periodValue?: number;
  isPlacedInHeader?: boolean;
  periodError?: string;
}

export const JointBillFilters: React.FC<IJointBillFiltersProps> = ({
  jointBillServiceOptions,
  jointBillServiceValue,
  periodOptions,
  periodValue,
  isPlacedInHeader,
  periodError,
}) => {
  const navigate = useNavigate();

  const monthPickerValue = useMemo(() => {
    const option = periodOptions?.find(
      (option) => option.value === periodValue,
    );

    if (!option) {
      return new Date();
    }

    return new Date(option?.year, option?.month - 1);
  }, [periodValue, periodOptions]);

  const onMonthPickerChange = (
    _: Date,
    dateInformation?: IMonthPickerAvailableDate,
  ) => {
    if (dateInformation?.href) {
      navigate(dateInformation.href);
    }
  };

  const dropdownJBSButton = useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    (props) => (
      <Button
        className="h-10 w-10 lg:hidden"
        variant="secondary"
        icon={HouseIcon}
        {...props}
      />
    ),
    [],
  );

  return (
    <React.Fragment>
      <div className="hidden gap-3 sm:flex">
        {periodOptions && (
          <>
            <div className="hidden lg:flex">
              <MonthPicker
                onChange={onMonthPickerChange}
                value={monthPickerValue}
                availableDates={periodOptions}
                error={periodError}
              />
            </div>
            <div className="flex lg:hidden">
              <MonthPicker
                onChange={onMonthPickerChange}
                value={monthPickerValue}
                availableDates={periodOptions}
              />
            </div>
          </>
        )}
        <Dropdown
          className="max-w-[160px]"
          options={jointBillServiceOptions}
          value={jointBillServiceValue}
          icon={HouseIcon}
          placement="bottom-end"
          isPositionFixed={isPlacedInHeader}
          containerClassName={!isPlacedInHeader ? "z-20" : "z-40"}
        />
      </div>
      <div className="flex gap-3 sm:hidden">
        {periodOptions && (
          <React.Fragment>
            <MonthPicker
              onChange={onMonthPickerChange}
              value={monthPickerValue}
              availableDates={periodOptions}
            />
          </React.Fragment>
        )}
        <Dropdown
          options={jointBillServiceOptions}
          value={jointBillServiceValue}
          buttonAs={dropdownJBSButton}
          placement="bottom-end"
          isPositionFixed={isPlacedInHeader}
          containerClassName={!isPlacedInHeader ? "z-20" : "z-40"}
        />
      </div>
    </React.Fragment>
  );
};
